/**
 * App entry point — /me-gated dispatch to app-shell or landing.
 *
 * Task 9: bootstrap() calls fetchMe(), then dynamically imports either
 * app-shell (logged-in) or views/landing/index (logged-out).
 *
 * Task 10 will expand mountAppShell() with the full renderApp() logic
 * (moved from this file's git history).
 */

import './styles/base.css';

import { fetchMe } from './auth/me-fetcher.js';
import { setState, loadBackfillUntilMs, getState } from './state/store.js';
import { setUnauthenticatedHandler, setCsrfTokenProvider } from './api/client.js';
import { setAskCsrfTokenProvider } from './api/ask.js';
import { demoUser } from './demo/data.js';
import { DEMO_MONTH, isDemoModeUrl } from './demo/mode.js';
import { resolveBestInitialMonth } from './utils/month-snap.js';

export async function bootstrap(): Promise<void> {
  // ── Public invite paths (no /me gate) ────────────────────────────
  // Dispatched BEFORE the /me fetch so unauthenticated visitors hit the
  // landing/error/declined views immediately without a wasted auth check.
  const path = window.location.pathname;
  const inviteMatch = /^\/invite\/([A-Za-z0-9_-]+)\/?$/.exec(path);
  if (inviteMatch) {
    const token = inviteMatch[1]!;
    const [{ mountInviteLanding }, { createApiClient }, { createHouseholdApi }] = await Promise.all([
      import('./views/invite-landing/index.js'),
      import('./api/client.js'),
      import('./api/household.js'),
    ]);
    const client = createApiClient({
      baseUrl: '',
      apiKey: (import.meta.env['VITE_API_KEY'] as string | undefined) ?? '',
    });
    mountInviteLanding({ api: createHouseholdApi(client), token });
    return;
  }
  if (path === '/invite-error') {
    const [{ mountInviteErrorView }, { createApiClient }, { createHouseholdApi }] =
      await Promise.all([
        import('./views/invite-error/index.js'),
        import('./api/client.js'),
        import('./api/household.js'),
      ]);
    const client = createApiClient({
      baseUrl: '',
      apiKey: (import.meta.env['VITE_API_KEY'] as string | undefined) ?? '',
    });
    mountInviteErrorView({ api: createHouseholdApi(client) });
    return;
  }
  if (path === '/invite-declined') {
    const [{ mountInviteDeclinedView }, { createApiClient }, { createHouseholdApi }] = await Promise.all([
      import('./views/invite-declined/index.js'),
      import('./api/client.js'),
      import('./api/household.js'),
    ]);
    const client = createApiClient({
      baseUrl: '',
      apiKey: (import.meta.env['VITE_API_KEY'] as string | undefined) ?? '',
    });
    mountInviteDeclinedView({ api: createHouseholdApi(client) });
    return;
  }

  if (isDemoModeUrl()) {
    setState({
      authStatus: 'logged-in',
      currentUser: demoUser,
      currentMonth: DEMO_MONTH,
      currentView: 'summary',
      accounts: [],
      insightsData: null,
      insightsCache: {},
      trendsCache: {},
      error: null,
    });
    const mod = await import('./app-shell.js');
    mod.mountAppShell({ mode: 'demo' });
    return;
  }

  let user = null;

  try {
    user = await fetchMe();
  } catch {
    // Network error — treat as logged-out
    user = null;
  }

  if (user !== null) {
    setState({
      authStatus: 'logged-in',
      currentUser: user,
      // Restore any in-progress post-link backfill deadline so the summary
      // view auto-refreshes if the user reloads mid-window.
      backfillUntilMs: loadBackfillUntilMs(user.id),
    });
    setUnauthenticatedHandler(() => {
      setState({ authStatus: 'logged-out', currentUser: null, backfillUntilMs: null });
      window.location.href = '/';
    });
    // The CSRF token is per-session HMAC; read fresh from the store on every
    // request so it picks up rotation if /me is re-fetched after a session
    // roll.
    const tokenProvider = (): string | null => getState().currentUser?.csrf_token ?? null;
    setCsrfTokenProvider(tokenProvider);
    setAskCsrfTokenProvider(tokenProvider);

    // Snap `currentMonth` to the most recent month with data BEFORE
    // mounting the app shell so the summary view's initial fetch hits the
    // right month — no "empty June" flash for West-Coast users when UTC
    // has already rolled over.
    //
    // The /months endpoint is fast (single GROUP BY over an indexed column)
    // and a network blip is non-fatal: we simply keep the UTC default and
    // the user can switch months manually.
    await snapInitialMonthToData();

    const mod = await import('./app-shell.js');
    mod.mountAppShell();
  } else {
    setState({ authStatus: 'logged-out', currentUser: null, backfillUntilMs: null });
    const mod = await import('./views/landing/index.js');
    mod.mountLanding();
  }
}

// Auto-run: skip in test environment to allow explicit bootstrap() calls in tests.
if (import.meta.env['MODE'] !== 'test') {
  if (document.readyState !== 'loading') {
    void bootstrap();
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      void bootstrap();
    });
  }
}

/**
 * Fetch the user's available months and update `currentMonth` to the most
 * recent month with data if the UTC-derived default isn't represented.
 *
 * Called from `bootstrap()` after `/me` resolves but before `mountAppShell`
 * imports — so the summary view's first render uses the correct month.
 *
 * Errors are swallowed: a network blip on bootstrap must not gate the
 * entire app. We keep the UTC default and the user can pick a month
 * manually via the header pill.
 */
async function snapInitialMonthToData(): Promise<void> {
  try {
    const res = await fetch('/api/v1/transactions/months', {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return;
    const body = (await res.json()) as { months?: unknown };
    const months = Array.isArray(body.months)
      ? body.months.filter((m): m is string => typeof m === 'string')
      : [];
    const current = getState().currentMonth;
    const snapped = resolveBestInitialMonth(months, current);
    if (snapped !== current) {
      setState({ currentMonth: snapped });
    }
  } catch {
    // Network/parse errors are non-fatal — keep UTC default month.
  }
}
